import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatAngularModule } from '../../modules/mat-angular.module';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string;
  /** Optional tooltip for the column header */
  tooltip?: string;
  /** Optional display format: 'percent' appends '%' to numeric values */
  format?: 'number' | 'percent';
}

export interface TableConfig {
  showHeader?: boolean;
  showCard?: boolean;
  highlightTopRows?: number;
  enableSorting?: boolean;
  enableHover?: boolean;
  cardClass?: string;
  tableClass?: string;
}

@Component({
  selector: 'app-common-simple-table',
  imports: [
    CommonModule,
    MatAngularModule
  ],
  templateUrl: './common-simple-table.component.html',
  styleUrl: './common-simple-table.component.scss'
})
export class CommonSimpleTableComponent {
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() title: string = 'Data Table';
  @Input() subtitle: string = '';
  @Input() config: TableConfig = {
    showHeader: true,
    showCard: true,
    highlightTopRows: 3,
    enableSorting: false,
    enableHover: true,
    cardClass: '',
    tableClass: ''
  };

  @Output() rowClick = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<{column: string, direction: 'asc' | 'desc'}>();

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  onRowClick(row: any): void {
    this.rowClick.emit(row);
  }

  onSort(column: TableColumn): void {
    if (!column.sortable || !this.config.enableSorting) return;

    if (this.sortColumn === column.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column.key;
      this.sortDirection = 'asc';
    }

    this.sortChange.emit({ column: this.sortColumn, direction: this.sortDirection });
  }

  getColumnAlignment(column: TableColumn): string {
    return column.align || 'left';
  }

  getColumnWidth(column: TableColumn): string {
    return column.width || 'auto';
  }

  getRowClasses(index: number): string[] {
    const classes: string[] = [];

    if (index < (this.config.highlightTopRows || 0)) {
      classes.push('highlight-row');
    }

    if (this.config.enableHover) {
      classes.push('hover-enabled');
    }

    return classes;
  }

  getHeaderClasses(column: TableColumn): string[] {
    const classes: string[] = ['table-header-cell'];

    if (column.sortable && this.config.enableSorting) {
      classes.push('sortable');
    }

    if (this.sortColumn === column.key) {
      classes.push('sorted');
    }

    return classes;
  }

  getCellClasses(column: TableColumn): string[] {
    return ['table-cell'];
  }

  formatCellValue(value: any, column: TableColumn): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'number') {
      if (column.format === 'percent') {
        return value + '%';
      }
      return value.toLocaleString();
    }

    return String(value);
  }

  /** Domain favicon URL (Google favicon service). Used for Source and URL columns. */
  getFaviconUrl(domain: string): string {
    if (!domain || typeof domain !== 'string') return '';
    const d = domain.trim().replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');
    return d ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(d)}&sz=32` : '';
  }

  /** Domain to use for favicon: source column uses row.source, url column uses row.domain. */
  getDomainForIcon(row: any, columnKey: string): string {
    if (columnKey === 'source') return row?.['source'] ?? '';
    if (columnKey === 'url') return row?.['domain'] ?? '';
    return '';
  }

  /** Hide favicon image when it fails to load (avoids broken image icon). */
  onFaviconError(event: Event): void {
    const el = event.target as HTMLImageElement;
    if (el) el.style.display = 'none';
  }

  /** Truncate URL for display in URL column (e.g. 25 chars + "…"); href stays full URL. */
  getTruncatedUrlDisplay(url: string, maxLen: number = 25): string {
    if (!url || typeof url !== 'string') return '';
    const s = url.trim();
    return s.length <= maxLen ? s : s.slice(0, maxLen) + '…';
  }

  /** Search URL for prompt/query (used for clickable prompt in URL column). */
  getSearchUrlForQuery(query: string): string {
    if (!query || typeof query !== 'string') return '#';
    return `https://www.google.com/search?q=${encodeURIComponent(query.trim())}`;
  }

  /** Safe class suffix for url_type chip (e.g. article, listicle, other). */
  getUrlTypeChipClass(row: any): string {
    const raw = row?.['url_type_raw'] ?? row?.['url_type'] ?? 'other';
    const s = String(raw).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '') || 'other';
    return s || 'other';
  }

  /** Safe class suffix for domain_type chip (e.g. corporate, ugc, editorial). */
  getDomainTypeChipClass(row: any): string {
    const raw = row?.['domain_type_raw'] ?? row?.['domain_type'] ?? 'other';
    const s = String(raw).toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'other';
    return s || 'other';
  }

  /** Chip class for brand_mentioned: yes (green), no (red), na (gray). */
  getBrandMentionedChipClass(row: any): string {
    const v = row?.['brand_mentioned_on_page'];
    if (v === true) return 'yes';
    if (v === false) return 'no';
    return 'na';
  }

  /** First letter (or two for multi-word) for competitor/brand chip avatar in Mentions column. */
  getBrandInitial(name: string): string {
    if (!name || typeof name !== 'string') return '?';
    const t = name.trim();
    if (!t) return '?';
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return t[0].toUpperCase();
  }

  // Stable tracking to avoid DOM re-creation and duplicate key warnings
  trackColumn = (index: number, column: TableColumn) => {
    return column?.key ?? `col-${index}`;
  };

  trackRow = (index: number, row: any) => {
    // Use only a guaranteed-unique id; domain/url can repeat (e.g. multiple pages per domain)
    return row?.id != null && String(row.id) !== '' ? String(row.id) : `row-${index}`;
  };
}
